/** Headless feedback modal: focus-trapped dialog with a submit→success/error FSM; host injects onSubmit + labels. */
import { XClose } from "@untitledui/icons";
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { Input } from "../Input";
import { Tab, TabGroup } from "../TabGroup";
import { Text } from "../Text";
import styles from "./FeedbackModal.module.css";

export type FeedbackCategory = "bug" | "idea" | "other";

export interface FeedbackPayload {
  category: FeedbackCategory;
  message: string;
  email?: string;
  /** Honeypot — always "" from a real user. */
  website: string;
}

export interface FeedbackModalLabels {
  title: string;
  close: string;
  messageLabel: string;
  messagePlaceholder: string;
  categoryLabel: string;
  categoryBug: string;
  categoryIdea: string;
  categoryOther: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailHelp: string;
  disclosure: string;
  submit: string;
  sending: string;
  successTitle: string;
  errorMessage: string;
  done: string;
}

export interface FeedbackModalProps {
  labels: FeedbackModalLabels;
  /** Async — host POSTs to /v1/feedback. Resolve = success; reject = error + preserve input. */
  onSubmit(payload: FeedbackPayload): Promise<void>;
  /** Close the dialog (host clears its open state). */
  onClose(): void;
}

type Status = "editing" | "sending" | "success" | "error";

export function FeedbackModal({ labels, onSubmit, onClose }: FeedbackModalProps) {
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState<FeedbackCategory>("other");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("editing");
  const sending = status === "sending";

  const titleId = useId();
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  // Focus the message field on open; restore focus to the opener on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    messageRef.current?.focus();
    return () => opener?.focus();
  }, []);

  // On success the form is swapped out, so focus the "Done" button to keep it inside the dialog (Button has no ref → query the rendered <button>).
  useEffect(() => {
    if (status === "success") successRef.current?.querySelector("button")?.focus();
  }, [status]);

  const handleSubmit = useCallback(async () => {
    if (message.trim().length === 0) return;
    setStatus("sending");
    try {
      await onSubmit({
        category,
        message: message.trim(),
        email: email.trim() === "" ? undefined : email.trim(),
        website,
      });
    } catch {
      setStatus("error");
      return;
    }
    setStatus("success");
  }, [message, category, email, website, onSubmit]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Ignore Escape mid-submit — closing would strand the later setStatus on an unmounted modal.
        if (sending) return;
        onClose();
        return;
      }
      // Focus trap (WAI-ARIA Dialog Pattern) — keep Tab inside the dialog.
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // Containment guard: pull focus back inside if it escaped (the first/last checks below assume it's already in).
      if (!dialog.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose, sending]
  );

  const canSubmit = message.trim().length > 0 && !sending;

  return (
    // The backdrop is a supplementary close affordance; Escape + the labelled
    // Close button are the primary, keyboard-accessible paths (both tested).
    // biome-ignore lint/a11y/noStaticElementInteractions: supplementary backdrop click
    // biome-ignore lint/a11y/useKeyWithClickEvents: supplementary mouse affordance — keyboard users close via the labeled Close button or Escape on the inner dialog
    <div
      className={styles.backdrop}
      onClick={() => {
        if (!sending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <Text as="h2" size="m" weight="semibold" id={titleId}>
            {labels.title}
          </Text>
          <IconButton
            icon={XClose}
            label={labels.close}
            variant="default"
            size="m"
            // Disabled mid-submit (like Escape + backdrop) so the later setStatus isn't stranded on an unmounted modal.
            disabled={sending}
            onClick={onClose}
          />
        </header>

        {status === "success" ? (
          <div ref={successRef} className={styles.successBody} role="status">
            <Text as="p" size="m" weight="medium">
              {labels.successTitle}
            </Text>
            <Button variant="primary" size="m" fullWidth onClick={onClose}>
              {labels.done}
            </Button>
          </div>
        ) : (
          <div className={styles.body}>
            <label className={styles.field}>
              <Text as="span" size="s" weight="medium">
                {labels.messageLabel}
              </Text>
              <textarea
                ref={messageRef}
                className={styles.textarea}
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (status === "error") setStatus("editing");
                }}
                placeholder={labels.messagePlaceholder}
                maxLength={4096}
                rows={4}
                disabled={sending}
              />
            </label>

            <div className={styles.field}>
              <Text as="span" size="s" weight="medium">
                {labels.categoryLabel}
              </Text>
              <TabGroup
                value={category}
                onChange={(v) => setCategory(v as FeedbackCategory)}
                aria-label={labels.categoryLabel}
                disabled={sending}
              >
                <Tab value="bug">{labels.categoryBug}</Tab>
                <Tab value="idea">{labels.categoryIdea}</Tab>
                <Tab value="other">{labels.categoryOther}</Tab>
              </TabGroup>
            </div>

            <Input
              label={labels.emailLabel}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={labels.emailPlaceholder}
              helpText={labels.emailHelp}
              disabled={sending}
            />

            <input
              className={styles.honeypot}
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />

            <Text as="p" size="xs" tone="tertiary" className={styles.disclosure}>
              {labels.disclosure}
            </Text>

            {status === "error" && (
              <Text as="p" size="s" tone="negative" role="alert">
                {labels.errorMessage}
              </Text>
            )}

            <Button
              variant="primary"
              size="m"
              fullWidth
              loading={sending}
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {sending ? labels.sending : labels.submit}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
